import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { NumericInput } from '../common/NumericInput';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import {
  classificationFromPurpose,
  classificationLabel,
  DEBT_PURPOSES,
  DEBT_TYPE_META,
  debtTypeLabel,
  INVESTMENT_RETURN_TYPES,
  investmentReturnTypeLabel,
  isInvestmentLosingMoney,
  PAYMENT_RECURRENCES,
  paymentRecurrenceLabel,
  purposeLabel,
} from './debtTypes';

const schema = z
  .object({
    name: z.string().min(1),
    type: z.string().min(1),
    purpose: z.enum(['necessity', 'desire', 'investment']),
    investment_return_type: z.string().optional().default(''),
    creditor: z.string().optional().default(''),
    debt_amount: z.string().min(1),
    interest_rate: z.string().optional().default(''),
    expected_annual_return: z.string().optional().default(''),
    monthly_payment: z.string().optional().default(''),
    payment_recurrence: z.enum(['monthly', 'quarterly', 'semi_annual', 'annual']),
    notes: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    if (data.purpose === 'investment' && !data.investment_return_type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['investment_return_type'],
        message: pt.liabilities.form.investmentReturnTypeRequired,
      });
    }
  });

const DEFAULTS = {
  name: '',
  type: 'personal_loan',
  purpose: 'necessity',
  investment_return_type: '',
  creditor: '',
  debt_amount: '',
  interest_rate: '',
  expected_annual_return: '',
  monthly_payment: '',
  payment_recurrence: 'monthly',
  notes: '',
};

function resolveTaeg(initialValues) {
  if (!initialValues) return '';
  const raw = initialValues.taeg ?? initialValues.annual_interest_rate;
  return raw != null ? String(raw) : '';
}

export function LiabilityFormDialog({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  isPending,
}) {
  const isEdit = Boolean(initialValues?.id);

  const { register, handleSubmit, reset, control, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
    mode: 'onChange',
  });

  const purpose = useWatch({ control, name: 'purpose', defaultValue: 'necessity' });
  const returnType = useWatch({ control, name: 'investment_return_type', defaultValue: '' });
  const taeg = useWatch({ control, name: 'interest_rate', defaultValue: '' });
  const expectedReturn = useWatch({ control, name: 'expected_annual_return', defaultValue: '' });
  const autoClassification = classificationFromPurpose(purpose, {
    returnType,
    taeg,
    expectedReturn,
  });
  const losingMoney = isInvestmentLosingMoney(purpose, returnType, taeg, expectedReturn);

  useEffect(() => {
    if (!isOpen) return;
    reset({
      ...DEFAULTS,
      ...(initialValues
        ? {
            name: initialValues.name || '',
            type: initialValues.type || 'personal_loan',
            purpose: initialValues.purpose || 'necessity',
            investment_return_type: initialValues.investment_return_type || '',
            creditor: initialValues.creditor || '',
            debt_amount: String(initialValues.current_balance ?? ''),
            interest_rate: resolveTaeg(initialValues),
            expected_annual_return:
              initialValues.expected_annual_return != null
                ? String(initialValues.expected_annual_return)
                : '',
            monthly_payment:
              initialValues.monthly_payment != null
                ? String(initialValues.monthly_payment)
                : '',
            payment_recurrence: initialValues.payment_recurrence || 'monthly',
            notes: initialValues.notes || '',
          }
        : {}),
    });
  }, [isOpen, initialValues, reset]);

  useEffect(() => {
    if (purpose !== 'investment') {
      setValue('investment_return_type', '');
      setValue('expected_annual_return', '');
    }
  }, [purpose, setValue]);

  const submit = (values) => {
    const isInvestment = values.purpose === 'investment';
    const isMonetary = isInvestment && values.investment_return_type === 'monetary';
    const payload = {
      name: values.name.trim(),
      type: values.type,
      purpose: values.purpose,
      investment_return_type: isInvestment ? values.investment_return_type : null,
      creditor: values.creditor?.trim() || '',
      debt_amount: values.debt_amount.replace(',', '.'),
      interest_rate_kind: 'taeg',
      interest_rate: values.interest_rate
        ? values.interest_rate.replace(',', '.')
        : null,
      monthly_payment: values.monthly_payment
        ? values.monthly_payment.replace(',', '.')
        : null,
      payment_recurrence: values.payment_recurrence,
      expected_annual_return:
        isMonetary && values.expected_annual_return
          ? values.expected_annual_return.replace(',', '.')
          : null,
      notes: values.notes || '',
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="flex max-h-[min(90dvh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="mb-0 shrink-0 px-5 pb-3 pt-5 pr-12 sm:px-6 sm:pt-6">
          <DialogTitle>
            {isEdit ? pt.liabilities.form.titleEdit : pt.liabilities.form.titleCreate}
          </DialogTitle>
          <DialogDescription>{pt.liabilities.form.subtitle}</DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={handleSubmit(submit)}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-5 py-1 sm:px-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground" htmlFor="liability-name">
                {pt.liabilities.form.nameLabel}
              </label>
              <Input
                id="liability-name"
                placeholder={pt.liabilities.form.namePlaceholder}
                {...register('name')}
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-muted-foreground">
                {pt.liabilities.form.typeLabel}
              </span>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-2 gap-2">
                    {DEBT_TYPE_META.map((item) => {
                      const ItemIcon = item.icon;
                      const selected = field.value === item.value;
                      return (
                        <button
                          type="button"
                          key={item.value}
                          onClick={() => field.onChange(item.value)}
                          className={cn(
                            'flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                            selected
                              ? 'border-sage-500 bg-sage-50 text-sage-800 dark:bg-sage-900/30 dark:text-sage-300'
                              : 'border-border bg-card text-muted-foreground hover:bg-muted',
                          )}
                        >
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundColor: `${item.color}22` }}
                          >
                            <ItemIcon
                              className="h-4 w-4"
                              style={{ color: item.color }}
                              aria-hidden
                            />
                          </span>
                          <span className="truncate">{debtTypeLabel(item.value)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-muted-foreground">
                {pt.liabilities.form.purposeLabel}
              </span>
              <Controller
                name="purpose"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {DEBT_PURPOSES.map((value) => {
                      const selected = field.value === value;
                      return (
                        <button
                          type="button"
                          key={value}
                          onClick={() => field.onChange(value)}
                          className={cn(
                            'rounded-xl border px-3 py-2.5 text-left text-sm transition-colors',
                            selected
                              ? 'border-sage-500 bg-sage-50 text-sage-800 dark:bg-sage-900/30 dark:text-sage-300'
                              : 'border-border text-muted-foreground hover:bg-muted',
                          )}
                        >
                          <span className="font-medium">{purposeLabel(value)}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {pt.liabilities.purposeHints[value]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              />
              {purpose !== 'investment' && autoClassification ? (
                <p className="text-xs text-muted-foreground">
                  {pt.liabilities.form.classificationAuto.replace(
                    '{value}',
                    classificationLabel(autoClassification),
                  )}
                </p>
              ) : null}
            </div>

            {purpose === 'investment' ? (
              <>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-muted-foreground">
                    {pt.liabilities.form.investmentReturnTypeLabel}
                  </span>
                  <Controller
                    name="investment_return_type"
                    control={control}
                    render={({ field }) => (
                      <div className="grid grid-cols-1 gap-2">
                        {INVESTMENT_RETURN_TYPES.map((value) => {
                          const selected = field.value === value;
                          return (
                            <button
                              type="button"
                              key={value}
                              onClick={() => {
                                field.onChange(value);
                                if (value !== 'monetary') {
                                  setValue('expected_annual_return', '');
                                }
                              }}
                              className={cn(
                                'rounded-xl border px-3 py-2.5 text-left text-sm transition-colors',
                                selected
                                  ? 'border-sage-500 bg-sage-50 text-sage-800 dark:bg-sage-900/30 dark:text-sage-300'
                                  : 'border-border text-muted-foreground hover:bg-muted',
                              )}
                            >
                              <span className="font-medium">
                                {investmentReturnTypeLabel(value)}
                              </span>
                              <span className="mt-0.5 block text-xs text-muted-foreground">
                                {pt.liabilities.investmentReturnTypeHints[value]}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                  {formState.errors.investment_return_type ? (
                    <p className="text-xs text-error" role="alert">
                      {formState.errors.investment_return_type.message}
                    </p>
                  ) : null}
                </div>

                {returnType === 'monetary' ? (
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-muted-foreground">
                      {pt.liabilities.form.expectedReturnLabel}
                    </label>
                    <Controller
                      name="expected_annual_return"
                      control={control}
                      render={({ field }) => (
                        <NumericInput
                          value={field.value ?? ''}
                          onChange={(event) => field.onChange(event.target.value)}
                          onBlur={field.onBlur}
                          ref={field.ref}
                        />
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      {pt.liabilities.form.expectedReturnOptional}
                    </p>
                  </div>
                ) : null}
              </>
            ) : null}

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                {pt.liabilities.form.debtAmountLabel}
              </label>
              <NumericInput {...register('debt_amount')} />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                {pt.liabilities.form.taegLabel}
              </label>
              <Controller
                name="interest_rate"
                control={control}
                render={({ field }) => (
                  <NumericInput
                    value={field.value ?? ''}
                    onChange={(event) => field.onChange(event.target.value)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
              <p className="text-xs text-muted-foreground">{pt.liabilities.form.taegHint}</p>
              {losingMoney ? (
                <p
                  className="rounded-lg border border-warning-500/30 bg-warning-tint px-3 py-2 text-xs text-warning-fg"
                  role="alert"
                >
                  {pt.liabilities.form.losingMoneyWarning}
                </p>
              ) : null}
              {purpose === 'investment' && returnType && autoClassification ? (
                <p
                  className={cn(
                    'text-xs font-medium',
                    autoClassification === 'good' ? 'text-sage-700' : 'text-muted-foreground',
                  )}
                >
                  {pt.liabilities.form.classificationAuto.replace(
                    '{value}',
                    classificationLabel(autoClassification),
                  )}
                </p>
              ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-muted-foreground">
                  {pt.liabilities.form.paymentAmountLabel}
                </label>
                <NumericInput {...register('monthly_payment')} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-muted-foreground">
                  {pt.liabilities.form.paymentRecurrenceLabel}
                </label>
                <Controller
                  name="payment_recurrence"
                  control={control}
                  render={({ field }) => (
                    <select
                      className="h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground"
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                    >
                      {PAYMENT_RECURRENCES.map((value) => (
                        <option key={value} value={value}>
                          {paymentRecurrenceLabel(value)}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                {pt.liabilities.form.creditorLabel}
              </label>
              <Input {...register('creditor')} />
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-border px-5 py-4 sm:px-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              {pt.common.cancel}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? pt.common.loading : pt.liabilities.form.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
